import { IInputElementProps, InputElement } from 'components/Input'

import { ErrorMessage } from 'components/v2/ErrorMessage'
import { ErrorMessage as FormikErrorMessage, getIn, useField } from 'formik'
import { ReactNode, forwardRef, useState } from 'react'
import styled from 'styled-components'
import { Label } from 'components/v2/Label'
import { Autocomplete } from 'components/v2/Autocomplete'
import { isEmpty } from 'lodash'
import { DEFAULT_COUNTRY_CODE, IPhoneCountryCode, countryCodes } from 'components/Formik/InputPhone/country-codes-list'

const Container = styled.div`
  display: flex;
  align-items: flex-start;
`
const InputContainer = styled.label`
  flex-grow: 1;
`

interface IPhoneInputFieldProps extends Omit<IInputElementProps<HTMLInputElement>, 'onChange'> {
  fieldName: string
  className?: string
  label?: ReactNode
  optional?: boolean
  onChange?: (value: { number: string; countryCode: string }) => void
}

const FORMIK_INPUT_PHONE_FIELD_NAMES = {
  number: 'number',
  countryCode: 'countryCode'
}

type ItemOption = IPhoneCountryCode & { id: string }

/**
 * `InputPhone` Component. This component is responsible for handling
 * phone number input which uses `useField` hook from Formik.
 *
 * The value in the Formik form values tied with this component's `fieldName` prop is an object structured:
 * {
 *  [fieldName]: {
 *    phone: string,
 *    countryCode: string
 *  }
 * }
 *
 *
 * @component
 * @param {IPhoneInputFieldProps} props - The properties that define the `InputPhone` component.
 * @param {string} props.fieldName - The name attribute that identifies the phone number input field
 *                            element in the Formik Form. This prop should correspond to a field in the
 *                            formik form `values` object.
 *
 * @example
 * // Example usage of `InputPhone`
 * <InputPhone fieldName="phoneNumber" />
 *
 * //Formik form initialValues example:
 * const initialValues = {
 *  // other form fields
 *    phoneNumber: {
 *       number: '',
 *       countryCode: ''
 *  }
 */
export const InputPhone = forwardRef<HTMLInputElement, IPhoneInputFieldProps>(function InputPhone(
  { fieldName, className, label, optional, ...inputProps }: IPhoneInputFieldProps,
  ref
) {
  const [field, meta, fieldHelper] = useField<{
    number: string
    countryCode: string
  }>(fieldName)
  const [countryCode, setCountryCode] = useState<{ dialCode: string; emoji: string; name: string; pattern: string }>({
    ...(countryCodes.get(field.value.countryCode || DEFAULT_COUNTRY_CODE) as IPhoneCountryCode),
    pattern: `+${field.value.countryCode || DEFAULT_COUNTRY_CODE}`
  })

  inputProps.onBlur = field.onBlur
  inputProps.name = fieldName
  inputProps.value = field.value.number

  const showError = Boolean(meta.touched && getIn(meta.error, FORMIK_INPUT_PHONE_FIELD_NAMES.number))
  const numberFieldName = `${fieldName}.${FORMIK_INPUT_PHONE_FIELD_NAMES.number}`

  const shownCountryCodeItems: ItemOption[] =
    Array.from(countryCodes.values())
      .map((item) => ({ ...item, id: item.code }))
      .filter((option) => {
        const pattern = countryCode.pattern.replace('+', '')
        const regExp = new RegExp('\\d')
        if (regExp.test(pattern)) {
          return option.dialCode.includes(pattern)
        } else {
          return option.name.toLocaleLowerCase().includes(pattern)
        }
      }) ?? []

  const resetCountryCodeToDefault = (): void => {
    setTimeout(() => {
      setCountryCode((prevCountryCode) => {
        const countryCode = countryCodes.get(prevCountryCode.pattern.replace('+', ''))
        if (isEmpty(countryCode)) {
          const defaultCountryCode = countryCodes.get(DEFAULT_COUNTRY_CODE)
          fieldHelper.setValue({
            number: field.value.number,
            countryCode: defaultCountryCode?.dialCode as string
          })
          return {
            ...(defaultCountryCode as IPhoneCountryCode),
            pattern: `+${defaultCountryCode?.dialCode as string}`
          }
        } else {
          fieldHelper.setValue({
            number: field.value.number,
            countryCode: countryCode.dialCode
          })
          return {
            ...countryCode,
            pattern: prevCountryCode.pattern
          }
        }
      })
    }, 300)
  }

  return (
    <div>
      {label && <Label optional={optional}>{label}</Label>}
      <Container>
        <Autocomplete
          style={{ width: '100px' }}
          value={countryCode.pattern}
          onChange={(value: IPhoneCountryCode, inputValue: { inputValue: string }): void => {
            if (value?.dialCode) {
              setCountryCode({
                ...value,
                pattern: `+${value.dialCode}`
              })
              fieldHelper.setValue({
                number: field.value.number,
                countryCode: value.dialCode
              })
            } else {
              setCountryCode({ dialCode: '', emoji: '', name: '', pattern: inputValue.inputValue })
            }
          }}
          onBlur={resetCountryCodeToDefault}
          items={shownCountryCodeItems}
          getItemLabel={(item: ItemOption): string => item.emoji + ' ' + `${item.name}` + ' ' + `(+${item.dialCode})`}
        />
        <InputContainer className={className}>
          <InputElement
            {...inputProps}
            type="tel"
            showError={showError}
            ref={ref}
            onChange={(e): void => {
              fieldHelper.setValue({
                number: e.target.value,
                countryCode: countryCode.dialCode
              })
            }}
            onBlur={(e): void => {
              e.target.name = numberFieldName
              field.onBlur(e)
            }}
          />
          <ErrorMessage>
            <FormikErrorMessage name={numberFieldName} />
          </ErrorMessage>
        </InputContainer>
      </Container>
    </div>
  )
})
